import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { MemoryTabs } from './MemoryTabs';
import { MemoryChat } from './MemoryChat';
import { MemoryChapters } from './MemoryChapters';
import { MemoryShare } from './MemoryShare';
import { Loader } from '../Loader';
import { Button } from '../Button';
import { useMemoryKeeper } from '../../shared/use-memory-keeper';
import { MEMORY_KEEPER_TOUR_TARGETS } from '../../../lib/shared/memory-keeper-tour-steps';
import { logEvent } from '../../../lib/shared/event-logger';
import { EVENT_TYPES } from '../../../lib/shared/event-types';
import styles from './MemoryKeeper.css';

const cx = classnames.bind(styles);

const TABS = [
    { type: 'chat', label: 'Chat', iconName: 'chat' },
    {
        type: 'memories',
        label: 'Memories',
        iconName: 'notebook',
        tourTarget: MEMORY_KEEPER_TOUR_TARGETS.MEMORIES_TAB
    },
    { type: 'share', label: 'Share', iconName: 'share' }
];

function MemoryKeeper({ activeTab, onTabChange, onReady }) {
    const {
        chapters,
        isLoadingChapters,
        isChaptersError,
        activeChapter,
        messages,
        isLoadingMessages,
        isChatError,
        isAssistantTyping,
        isChatFinished,
        sendErrorMessage,
        max,
        handleSelectChapter,
        handleSendMessage
    } = useMemoryKeeper({ onTabChange });

    useEffect(() => {
        if (!isLoadingChapters && !isChaptersError && !isLoadingMessages && !isChatError && activeChapter) {
            onReady();
        }
    }, [isLoadingChapters, isChaptersError, isLoadingMessages, isChatError, activeChapter, onReady]);

    const handleChaptersRetry = () => {
        logEvent(EVENT_TYPES.MEMORY_KEEPER_RETRY_CLICKED, { context: 'chapters' });
        window.location.reload();
    };

    const handleChatRetry = () => {
        logEvent(EVENT_TYPES.MEMORY_KEEPER_RETRY_CLICKED, { context: 'chat' });
        window.location.reload();
    };

    const renderChatContent = () => {
        if (!(activeTab === 'chat' && activeChapter)) {
            return null;
        }

        return (
            <MemoryChat
                chapters={chapters}
                activeChapter={activeChapter}
                onSelectChapter={handleSelectChapter}
                messages={messages}
                isLoadingMessages={isLoadingMessages}
                isChatError={isChatError}
                onRetry={handleChatRetry}
                isAssistantTyping={isAssistantTyping}
                isChatFinished={isChatFinished}
                sendErrorMessage={sendErrorMessage}
                onSendMessage={handleSendMessage}
            />
        );
    };

    const renderMainContent = () => {
        if (activeTab === 'share') {
            return <MemoryShare />;
        }

        if (isLoadingChapters) {
            return (
                <div className={styles.loaderWrapper}>
                    <Loader variant='secondary' />
                </div>
            );
        }

        if (isChaptersError) {
            return (
                <div className={styles.loaderWrapper}>
                    <div className={styles.errorState}>
                        <p className={styles.errorMessage}>Something went wrong loading your chapters.</p>
                        <Button
                            label='Try again'
                            type='primary'
                            onClick={handleChaptersRetry}
                            buttonProps={{ type: 'button' }}
                        />
                    </div>
                </div>
            );
        }

        return (
            <>
                {renderChatContent()}

                {activeTab === 'memories' && (
                    <MemoryChapters
                        chapters={chapters}
                        max={max}
                        onSelectChapter={handleSelectChapter}
                    />
                )}
            </>
        );
    };

    return (
        <div className={cx('root', { rootChatView: activeTab === 'chat' })}>
            <div className={styles.tabs}>
                <MemoryTabs
                    options={TABS}
                    value={activeTab}
                    onChange={onTabChange}
                />
            </div>

            {renderMainContent()}
        </div>
    );
}

MemoryKeeper.propTypes = {
    activeTab: PropTypes.string.isRequired,
    onTabChange: PropTypes.func.isRequired,
    onReady: PropTypes.func
};

MemoryKeeper.defaultProps = {
    onReady: () => {}
};

export {
    MemoryKeeper
};
